import { useEffect, useState, MouseEvent } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import DownOutlined from '@ant-design/icons/DownOutlined';
import BorderOutlined from '@ant-design/icons/BorderOutlined';

import NavItem from './NavItem';
import SimpleBar from 'components/third-party/SimpleBar';
import Transitions from 'components/@extended/Transitions';
import { useGetMenuMaster } from 'api/menu';
import { useAuth } from 'contexts/AuthContext';
import { NavItemType } from 'types/menu';
import { canAccess } from 'menu-items/access';
import { navAccentSx, navItemSx } from '../../sidebarStyles';

const PopperStyled = styled(Popper)(({ theme }) => ({
  overflow: 'visible',
  zIndex: 1202,
  minWidth: 200,
  '& > .MuiBox-root': {
    position: 'relative',
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 25,
      left: -5,
      width: 10,
      height: 10,
      background: theme.vars!.palette.background.paper,
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 120,
      borderLeft: '1px solid',
      borderLeftColor: theme.vars!.palette.divider,
      borderBottom: '1px solid',
      borderBottomColor: theme.vars!.palette.divider
    }
  }
}));

type NavCollapseProps = {
  menu: NavItemType;
  level?: number;
};

export default function NavCollapse({ menu, level = 1 }: NavCollapseProps) {
  const { menuMaster } = useGetMenuMaster();
  const { hasPermission, permissions } = useAuth();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const miniMenuOpen = Boolean(anchorEl);

  const filteredChildren: NavItemType[] = (menu.children || []).filter((child) => canAccess(child, { permissions, hasPermission }));

  const hasActiveChild = (items: NavItemType[]): boolean =>
    items.some((c) => {
      if (c.type === 'collapse') return !!c.children && hasActiveChild(c.children);
      if (c.url) return !!matchPath({ path: c.url, end: false }, pathname);
      return false;
    });

  useEffect(() => {
    if (drawerOpen && hasActiveChild(filteredChildren)) {
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, drawerOpen]);

  if (filteredChildren.length === 0) return null;

  const handleToggle = (e: MouseEvent<HTMLElement>) => {
    if (drawerOpen) {
      setOpen((prev) => !prev);
    } else {
      setAnchorEl(anchorEl ? null : e.currentTarget);
    }
  };

  const handleClose = () => setAnchorEl(null);

  // Linha em layout expandido: drawer aberto OU grupo aninhado (nível > 1).
  const rowOpen = drawerOpen || level !== 1;

  const Icon = menu.icon;
  const borderIcon = level === 1 ? <BorderOutlined style={{ fontSize: '1rem' }} /> : null;
  const menuIcon = Icon ? <Icon style={{ fontSize: rowOpen ? '1.125rem' : '1.25rem' }} /> : borderIcon;
  const isActive = hasActiveChild(filteredChildren);
  const expanded = drawerOpen ? open : miniMenuOpen;

  const renderChildren = () =>
    filteredChildren.map((child) => {
      if (child.type === 'collapse') {
        return <NavCollapse key={child.id} menu={child} level={level + 1} />;
      }
      if (child.type === 'item') {
        return <NavItem key={child.id} item={child} level={level + 1} />;
      }
      return null;
    });

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {isActive && <Box aria-hidden sx={(theme) => navAccentSx(theme, rowOpen)} />}
        <ListItemButton
          onClick={handleToggle}
          aria-expanded={expanded}
          sx={(theme) => navItemSx(theme, { open: rowOpen, level, active: isActive })}
        >
          {menuIcon && <ListItemIcon sx={{ minWidth: rowOpen ? 32 : 0, justifyContent: 'center' }}>{menuIcon}</ListItemIcon>}
          {rowOpen && (
            <ListItemText
              primary={
                <Typography
                  sx={(theme) => ({
                    fontSize: '0.8125rem',
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? theme.vars!.palette.primary.main : theme.vars!.palette.text.primary,
                    ...theme.applyStyles('dark', { color: isActive ? theme.vars!.palette.primary.light : theme.vars!.palette.text.primary })
                  })}
                >
                  {menu.title}
                </Typography>
              }
            />
          )}
          {rowOpen && (
            <Box
              component="span"
              sx={(theme) => ({
                display: 'inline-flex',
                color: expanded ? theme.vars!.palette.primary.main : theme.vars!.palette.text.secondary,
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create(['transform', 'color'], { duration: theme.transitions.duration.shorter }),
                ...theme.applyStyles('dark', { color: expanded ? theme.vars!.palette.primary.light : theme.vars!.palette.text.secondary })
              })}
            >
              <DownOutlined style={{ fontSize: '0.625rem' }} />
            </Box>
          )}
        </ListItemButton>
      </Box>

      {drawerOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List sx={{ p: 0 }}>{renderChildren()}</List>
        </Collapse>
      )}

      {!drawerOpen && (
        <PopperStyled open={miniMenuOpen} anchorEl={anchorEl} placement="right-start" style={{ zIndex: 2001 }}>
          {({ TransitionProps }) => (
            <Transitions in={miniMenuOpen} {...TransitionProps}>
              <Paper
                sx={(theme) => ({
                  ml: 1,
                  overflow: 'hidden',
                  borderRadius: 2,
                  backgroundImage: 'none',
                  boxShadow: theme.shadows[6],
                  border: '1px solid',
                  borderColor: 'divider'
                })}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <Box>
                    {menu.title && (
                      <Box sx={{ px: 1.75, py: 1.1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            fontWeight: 700,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: 'text.secondary'
                          }}
                        >
                          {menu.title}
                        </Typography>
                      </Box>
                    )}
                    <SimpleBar sx={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: '50vh' }}>
                      <List sx={{ p: 0.5, minWidth: 200 }}>{renderChildren()}</List>
                    </SimpleBar>
                  </Box>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </PopperStyled>
      )}
    </>
  );
}
